import { truncate } from 'lodash';
import { assertUnreachable } from '../enum';
import {
  CardActionStyle,
  CardResolution,
  InteractiveCard,
  InteractiveCardType,
} from './types';

export const SELECT_QUICK_REPLY_VERB = 'select_quick_reply';
export const MAX_QUICK_REPLY_OPTIONS = 5;
export const MAX_QUICK_REPLY_LABEL_LENGTH = 40;

export enum QuickReplyStyle {
  DEFAULT = 'DEFAULT',
  SUBTLE = 'SUBTLE',
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  SUCCESS = 'SUCCESS',
  CANCEL = 'CANCEL',
  DESTRUCTIVE = 'DESTRUCTIVE',
}

export const DEFAULT_QUICK_REPLY_STYLE = QuickReplyStyle.SECONDARY;

export type QuickReplyOption = {
  label: string;
  value: string;
  style?: QuickReplyStyle | null;
};

export type QuickReplySelection = {
  cardId: string;
  optionIndex: number;
};

export type BuildQuickReplyCardParams = {
  cardId: string;
  message: string;
  options: QuickReplyOption[];
  fallbackUrl: string;
  resolution?: CardResolution;
};

export function buildQuickReplyCard(
  params: BuildQuickReplyCardParams
): InteractiveCard {
  const { cardId, message, options, fallbackUrl, resolution } = params;
  return {
    cardType: InteractiveCardType.QUICK_REPLY,
    blocks: [{ type: 'markdown', text: message }],
    actions: options
      .slice(0, MAX_QUICK_REPLY_OPTIONS)
      .map((option, optionIndex) => ({
        verb: SELECT_QUICK_REPLY_VERB,
        label: formatQuickReplyLabel(option.label, optionIndex),
        style: toCardActionStyle(option.style ?? DEFAULT_QUICK_REPLY_STYLE),
        data: {
          cardId,
          optionIndex: String(optionIndex),
          value: option.value,
        },
        fallbackUrl,
      })),
    fallbackText: message,
    resolution,
  };
}

export function formatQuickReplyLabel(
  label: string,
  optionIndex: number
): string {
  const shortLabel = truncate(label.trim(), {
    length: MAX_QUICK_REPLY_LABEL_LENGTH,
  });
  return `${optionIndex + 1} · ${shortLabel}`;
}

export function toCardActionStyle(style: QuickReplyStyle): CardActionStyle {
  switch (style) {
    case QuickReplyStyle.DEFAULT:
      return 'default';
    case QuickReplyStyle.SUBTLE:
      return 'subtle';
    case QuickReplyStyle.PRIMARY:
      return 'primary';
    case QuickReplyStyle.SECONDARY:
      return 'secondary';
    case QuickReplyStyle.SUCCESS:
      return 'success';
    case QuickReplyStyle.CANCEL:
      return 'cancel';
    case QuickReplyStyle.DESTRUCTIVE:
      return 'danger';
    default:
      return assertUnreachable(style);
  }
}

export function isQuickReplyCard(card?: InteractiveCard): boolean {
  return card?.cardType === InteractiveCardType.QUICK_REPLY;
}

export function parseQuickReplySelection(
  data: unknown
): QuickReplySelection | null {
  const { cardId, optionIndex } = (data ?? {}) as Record<string, unknown>;
  const index = Number(optionIndex);
  if (typeof cardId !== 'string' || !Number.isInteger(index) || index < 0) {
    return null;
  }
  return { cardId, optionIndex: index };
}
